import React, { useCallback } from 'react'
import {
  Icon,
  Text,
  CheckboxField,
  constants,
  Alert,
  CardContainer,
} from '@adc/ui-core'
import type { CardContainerProps } from '@adc/ui-core'
import classnames from 'classnames/bind'
import { match } from 'ts-pattern'
import styles from './styles.scss'

const { ColorNames } = constants

const cx = classnames.bind(styles)

export const PHONE_NUMBER_START_RECEIVING = '650-667-7322'
const generateConsentMessage = (placement: string = '') => {
  return `You are signing your written consent to receive communications from Auction.com and its products like SmartSale, via text and phone at the number you provided${placement} and email, including by using an automated dialer or prerecorded or artificial message, for marketing and commercial purposes. Consent is not a condition of purchase. Message frequency varies. You may opt-out at any time. Message and data rates may apply.`
}

export const optInSMSText = {
  title: 'Want Smart Alerts While You Bid?',
  notVerifiedTitle:
    'Get real time updates on this transaction and more—straight to your phone.',
  verifiedTitle:
    'Get fast updates on bidding, credit bid reveals, and more—straight to your phone.',
  optedInStatusMessage:
    'Look out for Action Item text alerts related to your purchase.',
  optedOutStatusMessage: `You previously opted out of text alerts. To start receiving helpful alerts again text START to ${PHONE_NUMBER_START_RECEIVING}.`,
  consentMessageAbove: generateConsentMessage(' above'),
  consentMessageBelow: generateConsentMessage(' below'),
  consentMessage: generateConsentMessage(),
  benefitsTitle: "You'll Receive Action Items Like These:",
  footerText:
    'You may opt-out and manage your notifications at any time. Message and data rates may apply.',
  benefits: {
    foreclosureOnline: [
      'The property has been cleared for auction.',
      'The Est. Credit Bid has been revealed for your saved property.',
      'Auction is over, yet there are more opportunities to see.',
    ],
    default: [
      "You've Won! Please complete your contract form within 3 hours.",
      "A new auction is about to start with properties similar to ones you've viewed before. Check them out.",
      "You've been outbid. Submit a new bid to get the property you wanted.",
    ],
  },
}

type SMSOptInToggleProps = {
  optInStatus?: boolean | null
  initialOptInStatus?: boolean | null
  isVerified?: boolean
  onCheckBoxClick?: (checked: boolean) => void
  cardOverrideProps?: Omit<CardContainerProps, 'children'>
}

const SMSOptInToggle = ({
  initialOptInStatus,
  optInStatus = null,
  isVerified = false,
  onCheckBoxClick = () => {},
  cardOverrideProps = {},
}: SMSOptInToggleProps) => {
  const onChange = useCallback(() => {
    const newValue = !optInStatus
    onCheckBoxClick(newValue)
  }, [optInStatus, onCheckBoxClick])

  return match(initialOptInStatus)
    .with(true, () => (
      <div className={cx('sms-opted-in', 'u-mt-2')}>
        <Text color='gray' size='tiny'>
          {optInSMSText.optedInStatusMessage}
        </Text>
      </div>
    ))
    .with(false, () => (
      <Alert
        role='caution'
        content={optInSMSText.optedOutStatusMessage}
        showIcon
        iconName='mobile-signal'
      />
    ))
    .otherwise(() => (
      <CardContainer
        className={cx('u-mt-2')}
        borderRadius='medium'
        padding='medium'
        dropShadow='medium'
        {...cardOverrideProps}
      >
        <div className={cx('title-wrapper', 'u-mb-2')}>
          <Icon
            iconStyle='regular'
            name='mobile-signal'
            size='big'
            color={ColorNames.AdcLightGreen}
            className={cx('u-mr-2')}
          />
          <Text data-elm-id='sms_opt_in_title' size='huge' weight='heavy'>
            {optInSMSText.title}
          </Text>
        </div>
        <Text
          data-elm-id='sms_opt_in_subtitle'
          size='small'
          color={ColorNames.Gray}
          className={cx('u-mt-1', 'u-mb-2')}
        >
          {isVerified
            ? optInSMSText.verifiedTitle
            : optInSMSText.notVerifiedTitle}
        </Text>
        <div>
          <CheckboxField
            checked={Boolean(optInStatus)}
            elmId='sms_opt_in'
            label={
              <Text color='gray' size='tiny'>
                {`By checking this box, ${optInSMSText.consentMessage}`}
              </Text>
            }
            onChange={onChange}
          />
        </div>
      </CardContainer>
    ))
}

export default SMSOptInToggle
